import { useState, useCallback, useMemo } from 'react';
import styles from './Interactive.module.css';

interface MatchingPair {
  left: string;
  right: string;
}

interface MatchingConfig {
  pairs: MatchingPair[];
}

interface MatchingProps {
  config: MatchingConfig;
  onSubmit: (answer: string) => void;
  disabled: boolean;
  showResult: boolean;
}

export function Matching({ config, onSubmit, disabled, showResult }: MatchingProps) {
  const [matches, setMatches] = useState<Record<string, string>>({});
  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);

  // Shuffle right side items
  const shuffledRight = useMemo(() => {
    const items = config.pairs.map((p) => p.right);
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }, [config.pairs]);

  const handleLeftClick = useCallback((left: string) => {
    if (disabled || matches[left]) return;
    setSelectedLeft(left);
  }, [disabled, matches]);

  const handleRightClick = useCallback((right: string) => {
    if (disabled || !selectedLeft) return;

    // Check if right is already matched
    const alreadyMatched = Object.values(matches).includes(right);
    if (alreadyMatched) return;

    setMatches((prev) => ({
      ...prev,
      [selectedLeft]: right,
    }));
    setSelectedLeft(null);
  }, [disabled, selectedLeft, matches]);

  const handleSubmit = () => {
    if (disabled) return;
    onSubmit(JSON.stringify(matches));
  };

  const getLeftItemClass = (left: string) => {
    let className = styles.matchingItem;

    if (disabled) {
      className += ` ${styles.disabled}`;
    }

    if (matches[left]) {
      className += ` ${styles.matched}`;
    }

    if (selectedLeft === left) {
      className += ` ${styles.selected}`;
    }

    if (showResult) {
      const correctRight = config.pairs.find((p) => p.left === left)?.right;
      if (matches[left] === correctRight) {
        className += ` ${styles.correct}`;
      } else if (matches[left]) {
        className += ` ${styles.incorrect}`;
      }
    }

    return className;
  };

  const getRightItemClass = (right: string) => {
    let className = styles.matchingItem;

    if (disabled) {
      className += ` ${styles.disabled}`;
    }

    const isMatched = Object.values(matches).includes(right);
    if (isMatched) {
      className += ` ${styles.matched}`;
    }

    if (showResult) {
      // Find if this right item is correctly matched
      const correctPair = config.pairs.find((p) => p.right === right);
      if (correctPair && matches[correctPair.left] === right) {
        className += ` ${styles.correct}`;
      } else if (isMatched) {
        className += ` ${styles.incorrect}`;
      }
    }

    return className;
  };

  const allMatched = config.pairs.every((p) => matches[p.left]);

  return (
    <div className={styles.matchingContainer}>
      <div className={styles.matchingColumn}>
        <div className={styles.matchingColumnLabel}>Match these...</div>
        {config.pairs.map((pair, idx) => (
          <div
            key={idx}
            className={getLeftItemClass(pair.left)}
            onClick={() => handleLeftClick(pair.left)}
          >
            {pair.left}
            {matches[pair.left] && (
              <span style={{ marginLeft: '0.5rem', opacity: 0.6 }}>→</span>
            )}
          </div>
        ))}
      </div>

      <div className={styles.matchingLines}>
        {config.pairs.map((pair, idx) => (
          <div
            key={idx}
            className={`${styles.matchingLine} ${matches[pair.left] ? styles.connected : ''}`}
          >
            {matches[pair.left] ? '—' : '·'}
          </div>
        ))}
      </div>

      <div className={styles.matchingColumn}>
        <div className={styles.matchingColumnLabel}>...with these</div>
        {shuffledRight.map((right, idx) => (
          <div
            key={idx}
            className={getRightItemClass(right)}
            onClick={() => handleRightClick(right)}
          >
            {right}
          </div>
        ))}
      </div>

      {!showResult && (
        <div style={{ gridColumn: '1 / -1' }}>
          <div className={styles.inputContainer}>
            <button
              className={styles.submitButton}
              onClick={handleSubmit}
              disabled={disabled || !allMatched}
            >
              Submit Matches
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
